import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AccountEntity, SettingsEntity, TokenEntityStatusEnum } from '@interpals/api-web';

import { logout, clearState, saveToken } from '../auth/actions';
import { updateUserTags } from 'redux/tags/actions';
import { deleteAlbumPhoto, uploadPhotoToAlbumNew } from 'redux/photo-albums/actions';
import { createOAuth, deleteOAuth } from 'redux/oauth/actions';

import {
    updateAccount,
    updateAccountSettings,
    updateUserMatchSettings,
    updateAccountAbout,
    fetchAccountSettings,
    makeProfilePhoto,
    verifyAccount,
    getAccount,
} from './actions';

export interface AccountState {
    data: AccountEntity | null;
    settings: SettingsEntity | null;
    status: TokenEntityStatusEnum | null;
}

const initialState = {
    data: null,
    settings: null,
    status: null,
} as AccountState;

const accountSlice = createSlice({
    name: 'account',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(saveToken.fulfilled, (state, { payload }) => {
            const { account, status } = payload;

            state.data = account ?? null;
            state.status = status ?? null;
        });
        builder.addCase(logout.fulfilled, () => {
            return initialState;
        });
        builder.addCase(clearState.fulfilled, () => {
            return initialState;
        });
        builder.addCase(createOAuth.fulfilled, (state, payload) => {
            if (state.data?.oauth) {
                state.data.oauth[payload.meta.arg.provider] = true;
            }
        });
        builder.addCase(deleteOAuth.fulfilled, (state, { payload }) => {
            if (state.data?.oauth) {
                state.data.oauth[payload] = false;
            }
        });
        builder.addCase(updateAccount.fulfilled, (state, { payload: account }) => {
            if (state.data?.id === account.id) {
                state.data = account;
            }
        });
        builder.addCase(updateAccountAbout.fulfilled, (state, { payload: account }) => {
            if (state.data?.id === account.id) {
                state.data = account;
            }
        });
        builder.addCase(updateUserMatchSettings.fulfilled, (state, { payload: matchSettings }) => {
            if (state.data?.id === matchSettings.uid) {
                state.data.match_settings = matchSettings;
            }
        });
        builder.addCase(fetchAccountSettings.fulfilled, (state, { payload }) => {
            state.settings = payload;
        });
        builder.addCase(updateAccountSettings.fulfilled, (state, { payload: settings }) => {
            if (state.data?.id === settings.id || state.settings?.id === settings.id) {
                state.settings = settings;
            }
        });
        builder.addCase(makeProfilePhoto.fulfilled, (state, { payload }) => {
            if (state.data && payload?.cover) {
                state.data.avatar = payload.cover;
                state.data.profile_photos.album = payload;
            }
        });
        builder.addCase(updateUserTags.fulfilled, (state, { payload: tags, meta }) => {
            if (state.data && (!meta.arg.id || meta.arg.id === state.data?.id || meta.arg.id === 'self')) {
                state.data.user_tags = tags ?? [];
            }
        });
        builder.addCase(uploadPhotoToAlbumNew.fulfilled, (state, { payload }) => {
            if (payload && state.data?.profile_photos.album.id === payload?.aid) {
                if (!state.data.avatar) {
                    state.data.avatar = payload;
                }
                state.data.profile_photos.avatars = [...state.data.profile_photos.avatars, payload];
            }
        });
        builder.addCase(deleteAlbumPhoto.fulfilled, (state, { payload }) => {
            if (payload && state.data?.profile_photos.album.id === payload.albumId) {
                state.data.profile_photos.avatars = state.data.profile_photos.avatars.filter(
                    (photo) => photo.id !== payload.photoId,
                );
            }
        });
        builder.addMatcher(isAnyOf(verifyAccount.fulfilled, getAccount.fulfilled), (state, { payload }) => {
            state.data = payload;
        });
    },
});

export default accountSlice.reducer;
